import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';
import { Link } from 'react-router-dom';

const SOCIALS = [
  { Icon: Facebook, href: '#', label: 'Facebook' },
  { Icon: Twitter, href: '#', label: 'Twitter' },
  { Icon: Instagram, href: '#', label: 'Instagram' },
  { Icon: Linkedin, href: '#', label: 'LinkedIn' },
];

const PRODUCT_LINKS = [
  { label: 'Library', to: '/media' },
  { label: 'File Requests', to: '/file-requests' },
  { label: 'Quality Check', to: '/quality-check' },
  { label: 'Smart Collections', to: '/collections' },
  { label: 'Sharing', to: '/shared-by-me' },
];

const COMPANY_LINKS = [
  { label: 'Pear Media', href: '#' },
  { label: 'Contact', href: '#' },
  { label: 'Press', href: '#' },
];

const RESOURCE_LINKS = [
  { label: 'FAQ', to: '/faq' },
  { label: 'Privacy', href: '#' },
  { label: 'Terms', href: '#' },
  { label: 'Activity Audit', to: '/activity-logs' },
];

const APP_LINKS = [
  { label: 'Chrome Extension', href: '#' },
  { label: 'Slack', href: '#' },
];

/**
 * Section 5 — top spacer + parallax container with footer card on top.
 * Mirrors the spec layout (view-below spacer, h-screen parallax with bg
 * image, top-aligned footer card, parallax-driven foreground layer)
 * with the truck PNG replaced by an SVG folder-stack drawn inline.
 */
export function HomeFooterParallax() {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], [-50, 150]);

  return (
    <>
      {/* 5b — Parallax container */}
      <section
        ref={containerRef}
        className="relative h-screen overflow-hidden bg-black"
      >
        {/* Background video — locally-bundled, compressed from a 2.8 MB
            source down to ~120 KB WebM / 192 KB MP4. Poster paints
            instantly so users never see a black flash on slow networks. */}
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          controls={false}
          poster="/footer-poster.jpg"
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="/footer.webm" type="video/webm" />
          <source src="/footer.mp4" type="video/mp4" />
        </video>

        {/* Wash so the footer card stays legible regardless of the frame. */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-black/10 to-black/50" />

        {/* 5c — Top-aligned footer card */}
        <div className="absolute top-0 inset-x-0 z-30 px-6 pt-12 lg:pt-12 md:pt-24">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="max-w-7xl mx-auto bg-white/95 backdrop-blur-sm shadow-xl rounded-2xl md:rounded-3xl overflow-hidden"
          >
            {/* Top half — logo + columns */}
            <div className="p-8 md:p-12 flex flex-col md:flex-row gap-10 justify-between">
              {/* Logo block — uses the real /logo.png so it matches the rest
                  of the app. The ORBIT wordmark is already part of the asset,
                  so no separate text label is needed. */}
              <div className="flex flex-col gap-3">
                <img
                  src="/logo.png"
                  alt="Orbit by Pear Media"
                  className="h-12 md:h-14 w-auto object-contain object-left"
                />
                <p className="mt-3 text-sm text-gray-500 max-w-xs leading-relaxed">
                  The creative pipeline platform. Brief, deliver, review, launch — all in one library.
                </p>
              </div>

              {/* Link columns */}
              <div className="flex flex-wrap gap-10 md:gap-14">
                <FooterColumn title="Product" items={PRODUCT_LINKS} />
                <FooterColumn title="Company" items={COMPANY_LINKS} />
                <FooterColumn title="Resources" items={RESOURCE_LINKS} />
                <FooterColumn title="Apps" items={APP_LINKS} />
              </div>
            </div>

            {/* Bottom bar */}
            <div className="border-t border-gray-100 bg-white px-8 md:px-12 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <p className="text-sm text-gray-500 font-medium">
                © 2026 Pear Media LLC · Orbit. All rights reserved.
              </p>
              <div className="flex items-center gap-3">
                {SOCIALS.map(({ Icon, href, label }) => (
                  <a
                    key={label}
                    href={href}
                    aria-label={label}
                    className="w-10 h-10 rounded-full border border-gray-100 flex items-center justify-center text-gray-500 hover:bg-orange-500 hover:text-white hover:border-orange-500 transition-all duration-300"
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* 5d — Parallax foreground layer (folder stack, drawn inline) */}
        <motion.div
          style={{ y }}
          className="absolute inset-x-0 bottom-0 h-full pointer-events-none z-20 flex items-end justify-center"
        >
          <FolderStack className="w-full h-full object-contain object-bottom origin-bottom scale-[1.5] sm:scale-110 md:scale-[1.4] lg:scale-110" />
        </motion.div>
      </section>
    </>
  );
}

function FooterColumn({
  title,
  items,
}: {
  title: string;
  items: Array<{ label: string; to?: string; href?: string }>;
}) {
  return (
    <div>
      <h4 className="text-sm font-bold uppercase tracking-widest text-gray-700 mb-4">{title}</h4>
      <ul className="space-y-2.5">
        {items.map(item => (
          <li key={item.label}>
            {item.to ? (
              <Link
                to={item.to}
                className="text-sm text-gray-500 font-medium hover:text-orange-600 transition-colors"
              >
                {item.label}
              </Link>
            ) : (
              <a
                href={item.href}
                className="text-sm text-gray-500 font-medium hover:text-orange-600 transition-colors"
              >
                {item.label}
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Stack of three offset folders — pure SVG, no external image.
 * Colors match the section-2 folder palette so the brand stays consistent.
 */
function FolderStack({ className = '' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 800 600"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMax meet"
    >
      <defs>
        <filter id="folderShadow" x="-10%" y="-10%" width="120%" height="120%">
          <feDropShadow dx="0" dy="8" stdDeviation="12" floodColor="#0B1220" floodOpacity="0.18" />
        </filter>
        <linearGradient id="folder1" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#60A5FA" />
          <stop offset="1" stopColor="#2563EB" />
        </linearGradient>
        <linearGradient id="folder2" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#FB923C" />
          <stop offset="1" stopColor="#EA580C" />
        </linearGradient>
        <linearGradient id="folder3" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#34D399" />
          <stop offset="1" stopColor="#059669" />
        </linearGradient>
      </defs>

      {/* Back folder (purple-ish gray) */}
      <g filter="url(#folderShadow)" transform="translate(120, 200) rotate(-6 280 180)">
        <path
          d="M 0 40 Q 0 20 20 20 L 110 20 L 140 0 L 540 0 Q 560 0 560 20 L 560 340 Q 560 360 540 360 L 20 360 Q 0 360 0 340 Z"
          fill="#94A3B8"
        />
        <rect x="0" y="60" width="560" height="300" rx="20" fill="#CBD5E1" />
      </g>

      {/* Middle folder (blue) */}
      <g filter="url(#folderShadow)" transform="translate(180, 230) rotate(-2 280 180)">
        <path
          d="M 0 40 Q 0 20 20 20 L 110 20 L 140 0 L 540 0 Q 560 0 560 20 L 560 340 Q 560 360 540 360 L 20 360 Q 0 360 0 340 Z"
          fill="url(#folder1)"
        />
        <rect x="0" y="60" width="560" height="300" rx="20" fill="#DBEAFE" />
        {/* Doc rows */}
        <rect x="40" y="100" width="280" height="14" rx="3" fill="#93C5FD" />
        <rect x="40" y="130" width="220" height="10" rx="3" fill="#BFDBFE" />
        <rect x="40" y="155" width="320" height="10" rx="3" fill="#BFDBFE" />
        <rect x="40" y="180" width="180" height="10" rx="3" fill="#BFDBFE" />
      </g>

      {/* Front folder (orange) — slightly open */}
      <g filter="url(#folderShadow)" transform="translate(240, 250) rotate(2 280 180)">
        <path
          d="M 0 40 Q 0 20 20 20 L 110 20 L 140 0 L 540 0 Q 560 0 560 20 L 560 340 Q 560 360 540 360 L 20 360 Q 0 360 0 340 Z"
          fill="url(#folder2)"
        />
        <rect x="0" y="60" width="560" height="300" rx="20" fill="#FFF7ED" />
        {/* Media-card mockups inside */}
        <g transform="translate(40 100)">
          <rect width="150" height="100" rx="10" fill="#FED7AA" />
          <circle cx="75" cy="50" r="18" fill="#FB923C" />
          <polygon points="68,42 88,52 68,62" fill="#fff" />
        </g>
        <g transform="translate(210 100)">
          <rect width="150" height="100" rx="10" fill="#FED7AA" />
          <rect x="20" y="20" width="50" height="50" rx="4" fill="#FB923C" />
          <rect x="80" y="30" width="50" height="8" rx="2" fill="#FDBA74" />
          <rect x="80" y="46" width="40" height="8" rx="2" fill="#FDBA74" />
          <rect x="80" y="62" width="55" height="8" rx="2" fill="#FDBA74" />
        </g>
        <g transform="translate(380 100)">
          <rect width="150" height="100" rx="10" fill="#FED7AA" />
          <circle cx="75" cy="40" r="15" fill="#FB923C" />
          <rect x="40" y="65" width="70" height="6" rx="2" fill="#FDBA74" />
          <rect x="50" y="78" width="50" height="6" rx="2" fill="#FDBA74" />
        </g>
        {/* QC badge */}
        <g transform="translate(40 220)">
          <rect width="140" height="34" rx="17" fill="#10B981" />
          <circle cx="22" cy="17" r="8" fill="#fff" />
          <text x="40" y="22" fill="#fff" fontFamily="Inter, sans-serif" fontSize="13" fontWeight="700">
            QC PASSED
          </text>
        </g>
      </g>

      {/* Floating accent — small green tag */}
      <g transform="translate(640 320) rotate(8)" filter="url(#folderShadow)">
        <rect width="90" height="36" rx="18" fill="url(#folder3)" />
        <text x="45" y="23" textAnchor="middle" fill="#fff" fontFamily="Inter, sans-serif" fontSize="12" fontWeight="700">
          + new
        </text>
      </g>
    </svg>
  );
}
